// Landing page: a small "servers online" line from the battle server's
// /health (players online, queue). Hidden when the API can't be reached.

interface Health {
	status?: string;
	playersOnline?: number;
	queue?: number;
	liveMatches?: number;
}

async function serverStatus(): Promise<void> {
	const el = document.querySelector<HTMLElement>(".server-status");
	const text = document.querySelector<HTMLElement>(
		".server-status .server-text",
	);
	if (!el || !text) return;
	try {
		const cfg = (await (await fetch("/healthz")).json()) as { api?: string };
		if (!cfg.api) return;
		const res = await fetch(`${cfg.api.replace(/\/+$/, "")}/health`, {
			signal: AbortSignal.timeout(4000),
		});
		const h = (await res.json()) as Health;
		if (h.status !== "ok") throw new Error("down");
		const players =
			typeof h.playersOnline === "number" ? h.playersOnline : null;
		const live = typeof h.liveMatches === "number" ? h.liveMatches : null;
		const parts = ["Servers online"];
		if (players !== null)
			parts.push(`${players} ${players === 1 ? "player" : "players"}`);
		if (live) parts.push(`${live} ${live === 1 ? "match" : "matches"} live`);
		text.textContent = parts.join(" · ");
		el.hidden = false;
	} catch {
		// Unreachable or no CORS: say nothing rather than "offline".
	}
}

// "How it plays": the match video plays muted only while it's on screen, never
// with reduced motion (then its controls let the viewer start it).
function howVideo(): void {
	const v = document.querySelector<HTMLVideoElement>("video.how-video");
	if (!v) return;
	if (matchMedia("(prefers-reduced-motion: reduce)").matches) {
		v.controls = true;
		v.preload = "metadata";
		return;
	}
	if (!("IntersectionObserver" in window)) return;
	new IntersectionObserver(
		(entries) => {
			for (const e of entries) {
				if (e.isIntersecting) v.play().catch(() => {});
				else v.pause();
			}
		},
		{ threshold: 0.35 },
	).observe(v);
}

// The hero's battle loop: muted background footage. Not loaded at all with
// Save-Data or reduced motion (the cutout art stays: it is the poster and the fallback); paused off screen. Portrait and
// narrow screens get the 9:16 crop.
function heroVideo(): void {
	const v = document.querySelector<HTMLVideoElement>("video.hero-video");
	const hero = v?.closest<HTMLElement>(".hero");
	if (!v || !hero) return;
	const conn = (
		navigator as Navigator & { connection?: { saveData?: boolean } }
	).connection;
	if (conn?.saveData || matchMedia("(prefers-reduced-motion: reduce)").matches)
		return;
	const narrow = matchMedia(
		"(max-width: 760px), (orientation: portrait)",
	).matches;
	const src = narrow ? v.dataset.srcNarrow : v.dataset.srcWide;
	if (!src) return;
	v.src = src;
	v.addEventListener("playing", () => hero.classList.add("playing"), {
		once: true,
	});
	const play = () => v.play().catch(() => {});
	if (!("IntersectionObserver" in window)) {
		play();
		return;
	}
	new IntersectionObserver((entries) => {
		for (const e of entries) {
			if (e.isIntersecting) play();
			else v.pause();
		}
	}).observe(hero);
}

void serverStatus();
heroVideo();
howVideo();

export {};
